/**
 * Widget Style Contract
 *
 * Single source of truth for which CSS properties flow from Builder to widget iframes.
 * Consumed by:
 * - PreviewMessageHandler.tsx (Builder preview — PostMessage → CSS custom properties)
 * - styles.ts / collectAllCSS (live storefront — generated CSS classes)
 * - Future: Builder sidebar control filtering
 *
 * Adding a new customizable property: add it to the appropriate category below.
 * It will automatically be available in both preview and storefront paths.
 */

/**
 * Categorized allowlist of CSS properties that merchants can customize on widgets.
 * Categories are used for future sidebar UI grouping.
 * Property names are camelCase (matching Builder's block.getStyles() output).
 */
export const WIDGET_STYLE_CATEGORIES = {
  typography: [
    'fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
    'lineHeight', 'letterSpacing', 'textAlign', 'textTransform', 'textDecoration',
  ],
  colors: ['color', 'backgroundColor'],
  borders: [
    'borderWidth', 'borderStyle', 'borderColor', 'borderRadius',
    'borderTopLeftRadius', 'borderTopRightRadius',
    'borderBottomLeftRadius', 'borderBottomRightRadius',
  ],
  spacing: ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'],
  effects: ['boxShadow', 'opacity'],
} as const;

/** Category name type */
export type WidgetStyleCategory = keyof typeof WIDGET_STYLE_CATEGORIES;

/**
 * Flat allowlist derived from categories.
 * Readonly since source is `as const`.
 */
export const CUSTOMIZABLE_STYLE_PROPS: readonly string[] =
  Object.values(WIDGET_STYLE_CATEGORIES).flat();

/**
 * Set for O(1) membership lookups in filterCustomizableStyles.
 * Called once per PostMessage event — not in render path.
 */
export const CUSTOMIZABLE_STYLE_SET: ReadonlySet<string> =
  new Set(CUSTOMIZABLE_STYLE_PROPS);

/**
 * Per-widget property exclusions.
 *
 * All 6 header widgets have text in at least one variant
 * (e.g., HeaderCart "icon-text" shows "Cart" label), so blanket typography
 * exclusions are incorrect. Add exclusions only when a widget truly should
 * never receive a property in any variant.
 */
export const WIDGET_STYLE_EXCLUSIONS: Record<string, readonly string[]> = {
  // Empty for now. Example future usage:
  // 'widget-header-divider': ['fontFamily', 'fontSize', 'fontWeight', ...],
};

/**
 * Convert camelCase string to kebab-case.
 * e.g., "fontSize" → "font-size", "backgroundColor" → "background-color"
 */
function camelToKebab(str: string): string {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase();
}

/**
 * Convert a camelCase CSS property name to its --widget-* CSS custom property name.
 * e.g., "fontSize" → "--widget-font-size"
 */
export function toWidgetCSSVar(prop: string): string {
  return `--widget-${camelToKebab(prop)}`;
}

/**
 * Filter a raw styles object to only include customizable properties.
 * Non-allowlisted properties (display, position, etc.) are stripped.
 *
 * @param styles - Raw camelCase style object (from block.getStyles())
 * @param widgetElement - Optional widget element name for per-widget exclusions
 * @returns Filtered style object with only customizable properties
 */
export function filterCustomizableStyles(
  styles: Record<string, string>,
  widgetElement?: string,
): Record<string, string> {
  const exclusions = widgetElement
    ? WIDGET_STYLE_EXCLUSIONS[widgetElement]
    : undefined;

  const result: Record<string, string> = {};
  for (const [prop, value] of Object.entries(styles)) {
    if (!CUSTOMIZABLE_STYLE_SET.has(prop)) continue;
    if (exclusions?.includes(prop)) continue;
    if (value === '' || value === undefined || value === null) continue;
    result[prop] = value;
  }
  return result;
}

/**
 * Convert filtered styles to a CSS custom properties object.
 * Keys are --widget-* variable names, values are the CSS values.
 *
 * @param styles - Raw camelCase style object (will be filtered through allowlist)
 * @param widgetElement - Optional widget element name for per-widget exclusions
 * @returns Object like { '--widget-color': 'red', '--widget-font-size': '16px' }
 */
export function toWidgetCSSVars(
  styles: Record<string, string>,
  widgetElement?: string,
): Record<string, string> {
  const filtered = filterCustomizableStyles(styles, widgetElement);
  const vars: Record<string, string> = {};
  for (const [prop, value] of Object.entries(filtered)) {
    vars[toWidgetCSSVar(prop)] = value;
  }
  return vars;
}
